/**
 * Application bootstrap. Wires URL params → API → state → Rive (+ idle Pixi)
 * layers, then hands off to the orchestrator. Mirrors the init in
 *   packages/amgfootball_v2/lib/presentation/screens/game_screen.dart
 *
 * Notes:
 *  - Football's chip picker and goal-bet zones live INSIDE football.riv
 *    (ChipsLayer / BettingLayer artboards) and emit Rive events
 *    (evtClickChips / evtBetClick). Only the PLAY/CLEAR buttons are DOM.
 *  - The Pixi layer is present for the "Pixi + Rive" stack / perf benchmark
 *    but is currently idle (football has no chip-fly overlay).
 */
import { Api } from './net/api';
import { MultiplayerHub } from './net/multiplayerHub';
import { GameState } from './game/gameState';
import { GameOrchestrator } from './game/gameOrchestrator';
import { loadUrlParams } from './game/urlParams';
import { RiveLayer } from './rive/riveLayer';
import { PixiLayer } from './pixi/pixiLayer';
import { Hud } from './ui/hud';
import { TableControls } from './ui/controls';
import { GameTimerWidget } from './ui/timerWidget';
import { GameLogWidget } from './ui/gameLogWidget';
import { PerfHud } from './perf/perfHud';
import { ResponsiveScaler } from './util/responsiveScaler';
import { MULTIPLAYER_HUB_URL } from './config';
import { debugLog, error as logError } from './util/logger';

declare global {
  interface Window {
    // Defined by the inline loading-splash controller in index.html.
    hideLoadingScreen?: () => void;
  }
}

async function bootstrap(): Promise<void> {
  if (!('WebGL2RenderingContext' in window)) {
    showFatal('Your browser does not support WebGL2. Please use a modern browser.');
    return;
  }

  const scaler = new ResponsiveScaler('stage');
  scaler.attach();

  const params = loadUrlParams();
  debugLog('[main] URL params', params);

  const riveMain = document.getElementById('rive-canvas-main') as HTMLCanvasElement;
  const riveBetting = document.getElementById('rive-canvas-betting') as HTMLCanvasElement;
  const riveChips = document.getElementById('rive-canvas-chips') as HTMLCanvasElement;
  const pixiCanvas = document.getElementById('pixi-canvas') as HTMLCanvasElement;
  if (!riveMain || !riveBetting || !riveChips || !pixiCanvas) {
    showFatal('Stage canvases missing from DOM.');
    return;
  }

  const rive = new RiveLayer({
    canvases: { main: riveMain, betting: riveBetting, chips: riveChips },
  });
  const pixi = new PixiLayer(pixiCanvas);

  const perfHud = new PerfHud();
  perfHud.start();

  const state = new GameState();
  const api = new Api();
  const hub = new MultiplayerHub(MULTIPLAYER_HUB_URL);
  const hud = new Hud();
  const timer = new GameTimerWidget();
  void new GameLogWidget();

  // Deferred ref so the DOM controls can call into the orchestrator.
  let orchestratorRef: GameOrchestrator | null = null;
  const controls = new TableControls({
    onClear: () => orchestratorRef?.clearAll(),
    onConfirm: () => void orchestratorRef?.confirm(),
  });

  try {
    await Promise.all([pixi.init(), rive.init()]);
  } catch (e) {
    logError('[main] init failed', e);
    showFatal(`Failed to load assets: ${(e as Error)?.message ?? String(e)}`);
    return;
  }
  state.setRiveReady(true);

  const orchestrator = new GameOrchestrator({
    state,
    api,
    rive,
    hud,
    controls,
    hub,
    timer,
    params,
    onInteractive: () => {
      perfHud.markInteractive();
      // Game is signed in, configured and rendering — drop the loading splash.
      window.hideLoadingScreen?.();
    },
  });
  orchestratorRef = orchestrator;

  await orchestrator.start();
}

function showFatal(message: string): void {
  // Drop the loading splash first — it sits above the fatal overlay.
  window.hideLoadingScreen?.();
  const overlay = document.getElementById('fatal-overlay');
  const msg = document.getElementById('fatal-message');
  if (msg) msg.textContent = message;
  if (overlay) overlay.style.display = 'flex';
}

window.addEventListener('error', (e) => logError('[window.error]', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) =>
  logError('[unhandledrejection]', e.reason),
);

bootstrap().catch((e) => {
  logError('[main] bootstrap crashed', e);
  showFatal(`Bootstrap failed: ${(e as Error)?.message ?? String(e)}`);
});
